import React, { useReducer } from 'react';
import styled from 'styled-components';
import { Link as RouterLink } from 'react-router-dom';

import { TextField, Divider, Typography, Link } from '@material-ui/core';

import { Auth } from 'aws-amplify';
import { PRIVACY_POLICY_PATH } from '../../routes';

import BtnPrimary from '../common/BtnPrimary';
import CheckboxPrimary from '../common/CheckboxPrimary';
import IconLock from '../../assets/icon-lock.svg';
import PasswordStrengthMeter from '../common/PasswordStrengthMeter';
import InputPassword from '../common/inputs/InputPassword';

const Root = styled.div`
	a {
		color: ${(props) => props.theme.palette.text.link};
		text-decoration: underline;
	}

	.register__checkbox {
		font-size: 14px;
		display: flex;
		align-items: center;

		&:not(:first-child) {
			margin-top: 10px;
		}
	}

	.register__submit {
		margin: 20px 0;
		text-align: right;
	}

	.register__privacy-info {
		padding: 0 0 20px 0;

		.is-heading {
			display: flex;
			align-items: center;
			font-weight: bold;
			margin-bottom: 20px;

			img {
				width: 25px;
				margin-right: 20px;
			}
		}
	}

	.error {
		margin: 20px 0 10px 0;
		padding: 20px;
		background-color: ${(props) => props.theme.palette.background.error};
		color: ${(props) => props.theme.palette.text.primary};
		font-size: 16px;
		border-radius: 5px;
	}

	.MuiFormControl-root {
		margin: 20px 0 0 0;
	}
`;

const FormDivider = styled(Divider)`
	&.MuiDivider-root {
		margin: 40px 0;
		background-color: ${(props) => props.theme.palette.background.secondary};
	}
`;

function registerReducer(state, action) {
	switch (action.type) {
		case 'field': {
			return {
				...state,
				[action.fieldName]: action.payload,
			};
		}
		case 'register': {
			return {
				...state,
				error: '',
				isLoading: true,
			};
		}
		case 'success': {
			return {
				...state,
				isLoading: false,
			};
		}
		case 'error': {
			return {
				...state,
				error: action.error,
				isLoading: false,
				password: '',
			};
		}
		default:
			return state;
	}
}

const initialState = {
	username: '',
	password: '',
	email: '',
	isLoading: false,
	error: '',
	onChangeAGB: false,
	onChangeNewsletter: false,
};

export default function FormRegister({
	closeRegisterModal,
	openLoginModal,
	showSuccessScreen,
}) {
	const [state, dispatch] = useReducer(registerReducer, initialState);

	const { username, password, email, isLoading, error, onChangeNewsletter } = state;

	const onSubmit = async (e) => {
		e.preventDefault();
		try {
			const awsUser = await Auth.signUp({
				username,
				password,
				attributes: {
					email,
					'custom:Newsletter': `${onChangeNewsletter}`,
				},
			});
			console.log('current user', awsUser);
			dispatch({ type: 'success' });
			showSuccessScreen();
			closeRegisterModal();
		} catch (error) {
			dispatch({ type: 'error', error: error.message });
			console.log('error signing up:', error);
		}
	};

	const onChange = (e) => {
		dispatch({
			type: 'field',
			fieldName: e.target.name,
			payload: e.target.checked,
		});
	};

	const dataPrivacy = (
		<RouterLink target="_blank" to={PRIVACY_POLICY_PATH}>
			Datenschutzerklärung
		</RouterLink>
	);

	const openLoginForm = (
		<Link
			href=""
			onClick={() => {
				closeRegisterModal();
				openLoginModal();
			}}
		>
			anmelden
		</Link>
	);

	return (
		<Root>
			<form onSubmit={onSubmit}>
				{error && (
					<div className="error">
						<span>{error}</span>
					</div>
				)}
				<TextField
					value={username}
					variant="outlined"
					margin="normal"
					required
					fullWidth
					label="Benutzername"
					onChange={(e) =>
						dispatch({
							type: 'field',
							fieldName: 'username',
							payload: e.currentTarget.value,
						})
					}
				/>
				<TextField
					value={email}
					variant="outlined"
					margin="normal"
					type="email"
					required
					fullWidth
					label="E-Mail-Adresse"
					onChange={(e) =>
						dispatch({
							type: 'field',
							fieldName: 'email',
							payload: e.currentTarget.value,
						})
					}
				/>

				<InputPassword
					passwordValue={password}
					dispatch={(e) =>
						dispatch({
							type: 'field',
							fieldName: 'password',
							payload: e.currentTarget.value,
						})
					}
				/>

				<PasswordStrengthMeter StringtoCheck={password} />

				<div className="register__checkbox">
					<CheckboxPrimary required onChange={onChange} name="onChangeAGB" />
					<Typography>Ich akzeptiere die {dataPrivacy}.*</Typography>
				</div>

				<div className="register__checkbox">
					<CheckboxPrimary onChange={onChange} name="onChangeNewsletter" />
					<Typography>
						Ich möchte über be.storaged-Neuigkeiten auf dem Laufenden bleiben.
					</Typography>
				</div>

				<div className="register__submit">
					<BtnPrimary type="submit" variant="contained" disabled={isLoading}>
						{isLoading ? 'Registrierung läuft...' : 'Registrieren'}
					</BtnPrimary>
				</div>
			</form>

			<FormDivider />

			<Typography variant="body2">Mit bestehenden Konto {openLoginForm}</Typography>

			<FormDivider />

			<div className="register__privacy-info">
				<div className="is-heading">
					<img src={IconLock} alt="Icon Lock" />
					<span>Ihre persönlichen Daten werden geschützt!</span>
				</div>
				<Typography variant="body2">
					Wir gewährleisten den größtmöglichen Schutz Ihrer persönlichen Daten
					und geben sie nicht an Dritte weiter.
				</Typography>
			</div>
		</Root>
	);
}
